import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Search } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CLIENT_MODULE_SCOPES } from "@/lib/clientModuleScope";
import { setStoredClientId } from "@/lib/clientSelection";
import {
  EMPTY_CLIENTS_ONBOARDING_MESSAGE,
  LICENSE_INACTIVE_SELECTOR_HINT,
  buildEmptyClientsOnboardingActions,
} from "@/modules/panelHubOnboarding";
import { CONFIGURACION_DESPACHO_PATH } from "@/modules/config/configuracionRoutes";

/** Propaga el cliente predeterminado a todos los módulos operativos. */
export function setHubDefaultClientId(clientId) {
  setStoredClientId(clientId || "", CLIENT_MODULE_SCOPES.VISOR_SAT);
}

/**
 * Selector global de cliente de trabajo fiscal (colección clients / Visor SAT).
 * Clientes operativos del despacho viven en /despacho/clientes — ver CTAs cuando no hay fiscales.
 */
export default function PanelHubClientSelector({
  clients,
  clientId,
  onSelect,
  hasDespacho = false,
  hasVisor = false,
  licenseInactive = false,
}) {
  const selected = clients.find((c) => c.id === clientId);
  const [expanded, setExpanded] = useState(false);
  const [searchQ, setSearchQ] = useState("");

  useEffect(() => {
    if (!expanded) setSearchQ("");
  }, [expanded]);

  const filteredClients = useMemo(() => {
    const q = searchQ.trim().toUpperCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const blob = [c.razon_social, c.nombre_comercial, c.rfc, c.email]
        .filter(Boolean)
        .join(" ")
        .toUpperCase();
      return blob.includes(q);
    });
  }, [clients, searchQ]);

  const handleSelect = (id) => {
    onSelect(id);
    setExpanded(false);
    setSearchQ("");
  };

  const emptyActions = useMemo(
    () => buildEmptyClientsOnboardingActions({ hasDespacho, hasVisor }),
    [hasDespacho, hasVisor],
  );

  return (
    <div
      className="bg-white border border-slate-200 rounded-sm w-full min-w-0 h-full"
      data-testid="panel-hub-client-selector"
    >
      <Accordion
        type="single"
        collapsible
        value={expanded ? "cliente-trabajo" : ""}
        onValueChange={(value) => setExpanded(value === "cliente-trabajo")}
      >
        <AccordionItem value="cliente-trabajo" className="border-0">
          <AccordionTrigger
            className="px-3 py-2.5 hover:no-underline [&[data-state=open]]:pb-2 h-full min-h-[3.25rem]"
            data-testid="panel-hub-client-selector-trigger"
          >
            <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left pr-2">
              <span className="text-xs font-semibold text-[#0F2D5C]">Cliente de trabajo</span>
              {selected ? (
                <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-sm font-semibold text-[#0F2D5C] truncate max-w-full">
                    {selected.razon_social || "Sin razón social"}
                  </span>
                  <span className="text-xs font-mono text-slate-600 shrink-0">{selected.rfc}</span>
                  <span className="text-[9px] font-bold uppercase tracking-wide text-[#3DA873]">Activo</span>
                </span>
              ) : (
                <span className="text-[10px] text-slate-500 font-normal">
                  {clients.length === 0
                    ? "Sin clientes fiscales — ver opciones al expandir"
                    : "Selecciona un cliente fiscal de la lista"}
                </span>
              )}
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 pt-0 border-t border-slate-200">
            <p className="text-xs text-slate-600 mt-3 mb-3">
              Elige con cuál <strong>cliente fiscal (Visor SAT)</strong> vas a operar en carga, visores y
              confronta. Los <strong>clientes operativos del despacho</strong> se gestionan aparte.
            </p>
            {clients.length === 0 ? (
              <div
                className="rounded-sm border border-slate-200 bg-white px-4 py-4 mb-3 space-y-3"
                data-testid="panel-hub-empty-clients-onboarding"
              >
                <p className="text-sm text-slate-700 m-0">{EMPTY_CLIENTS_ONBOARDING_MESSAGE}</p>
                {licenseInactive ? (
                  <p
                    className="text-xs text-amber-900 m-0 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2"
                    data-testid="panel-hub-license-inactive-hint"
                  >
                    {LICENSE_INACTIVE_SELECTOR_HINT}{" "}
                    <Link
                      to={`${CONFIGURACION_DESPACHO_PATH}?solicitar_licencia=1`}
                      className="font-semibold text-[#0F2D5C] underline"
                    >
                      Ir a Configuración
                    </Link>
                  </p>
                ) : null}
                {emptyActions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {emptyActions.map((action) => (
                      <Button
                        key={action.id}
                        asChild
                        variant={action.id === "despacho" ? "default" : "outline"}
                        size="sm"
                        className={
                          action.id === "despacho"
                            ? "rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]"
                            : "rounded-sm"
                        }
                      >
                        <Link to={action.to} data-testid={action.testId}>
                          {action.label}
                        </Link>
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 m-0">
                    Contrata Visor SAT o Gestión Despacho para registrar clientes en este espacio.
                  </p>
                )}
              </div>
            ) : null}
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                type="search"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Buscar por nombre o RFC…"
                className="pl-8 h-9 text-sm bg-white"
                data-testid="panel-hub-client-search"
                aria-label="Buscar cliente por nombre o RFC"
              />
            </div>
            {clients.length > 0 && searchQ.trim() ? (
              <p className="text-[11px] text-slate-500 mb-2">
                {filteredClients.length} de {clients.length} cliente(s)
              </p>
            ) : null}
            <ul className="max-h-60 overflow-y-auto rounded-sm border border-slate-200 bg-white divide-y divide-slate-100">
              {filteredClients.length === 0 ? (
                <li className="px-4 py-6 text-center text-sm text-slate-500">
                  {clients.length === 0
                    ? "No hay clientes fiscales registrados todavía."
                    : "Ningún cliente coincide con la búsqueda."}
                </li>
              ) : (
                filteredClients.map((c) => {
                  const active = c.id === clientId;
                  const initials = (c.razon_social || c.rfc || "?")
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase() || "?";
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(c.id)}
                        data-testid={`panel-hub-client-row-${c.id}`}
                        className={[
                          "w-full text-left px-4 py-3 flex items-center gap-3 transition-colors",
                          active ? "bg-emerald-50/80 hover:bg-emerald-50" : "hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border text-xs font-bold",
                            active
                              ? "border-[#3DA873] bg-[#0F2D5C] text-white"
                              : "border-slate-200 bg-slate-50 text-slate-500",
                          ].join(" ")}
                          aria-hidden
                        >
                          {active ? <CheckCircle2 className="w-4 h-4" /> : initials.slice(0, 2)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-[#0F2D5C] truncate">
                            {c.razon_social || "Sin razón social"}
                          </span>
                          <span className="block text-xs font-mono text-slate-600 truncate">{c.rfc}</span>
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
            <div className="flex flex-wrap items-center justify-between gap-2 pt-3">
              <p className="text-[11px] text-slate-500">
                {clientId
                  ? "Cliente fiscal predeterminado para Visor, Laboral y Trámites."
                  : clients.length > 0
                    ? "Selecciona un cliente fiscal de la lista."
                    : "Sin clientes fiscales — usa los accesos de arriba para empezar."}
              </p>
              {hasVisor && clients.length > 0 ? (
                <Link to="/clientes" className="text-xs text-[#0F2D5C] font-medium underline shrink-0">
                  Gestionar clientes fiscales
                </Link>
              ) : null}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
